import jsPDF from "jspdf";
import html2canvas from "html2canvas";

// Renders a DOM element into a multi-page A4 PDF and returns it as a Blob.
// One html2canvas capture, then sliced into page-height chunks so long
// statements/tables span multiple pages instead of being cut off.
export async function elementToPdfBlob(el, { marginMm = 8 } = {}) {
  const canvas = await html2canvas(el, {
    scale: 2,
    useCORS: true,
    backgroundColor: "#ffffff",
  });

  const pdf = new jsPDF({ unit: "mm", format: "a4" });
  const pageWmm = pdf.internal.pageSize.getWidth();
  const pageHmm = pdf.internal.pageSize.getHeight();
  const contentWmm = pageWmm - marginMm * 2;
  const contentHmm = pageHmm - marginMm * 2;

  const pxToMm = contentWmm / canvas.width;
  const pageHeightPx = contentHmm / pxToMm;

  let renderedPx = 0;
  let first = true;
  while (renderedPx < canvas.height) {
    const sliceHeightPx = Math.min(pageHeightPx, canvas.height - renderedPx);

    const pageCanvas = document.createElement("canvas");
    pageCanvas.width = canvas.width;
    pageCanvas.height = sliceHeightPx;
    pageCanvas.getContext("2d").drawImage(
      canvas, 0, renderedPx, canvas.width, sliceHeightPx, 0, 0, canvas.width, sliceHeightPx
    );

    if (!first) pdf.addPage();
    pdf.addImage(pageCanvas.toDataURL("image/jpeg", 0.95), "JPEG",
      marginMm, marginMm, contentWmm, sliceHeightPx * pxToMm);

    renderedPx += sliceHeightPx;
    first = false;
  }

  return pdf.output("blob");
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Shares a PDF (built from `el`) as an actual file attachment via the
// device's native share sheet (Android/iOS — pick WhatsApp there and the
// PDF attaches directly). Desktop browsers don't support sharing files
// this way, so there we fall back to downloading the PDF and opening
// WhatsApp with the text pre-filled, for the user to attach manually.
export async function sharePdfOnWhatsapp({ el, fileName, text }) {
  const blob = await elementToPdfBlob(el);
  const file = new File([blob], fileName, { type: "application/pdf" });

  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: fileName, text: text || "" });
      return "shared";
    } catch (e) {
      if (e?.name === "AbortError") return "cancelled";
      // fall through to the download+wa.me fallback below
    }
  }

  downloadBlob(blob, fileName);
  window.open(`https://wa.me/?text=${encodeURIComponent(text || "")}`, "_blank");
  // File sharing only exists on secure pages (https, or localhost). Over
  // plain http -- e.g. a dev build opened via this machine's LAN IP --
  // navigator.share is missing entirely, so no browser can attach the PDF.
  return window.isSecureContext ? "fallback" : "insecure";
}
